/**
 * Where a linked code repository is cloned ON THIS DEVICE (commit ↔ task
 * links, slice 1). A repository linked by URL (every team repository) has no
 * folder in the synced data — a folder names one person's disk — so the user
 * points at their clone once per device. Kept in localStorage per account
 * (`apibooKey`), never synced. Storage that throws or holds junk reads as
 * "not picked".
 */
import { apibooKey } from '../storageNamespace';
import { ownerRepoOf } from './providers/remote';
import { hostOf } from '../../graph/repoRefs';

const SUFFIX = 'code-repo-clones';

function readAll() {
  try {
    const raw = window.localStorage.getItem(apibooKey(SUFFIX));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/** The folder picked for repo `repoId` on this device, or ''. */
export function clonePathFor(repoId) {
  const v = readAll()[String(repoId)];
  return typeof v === 'string' ? v : '';
}

/** Remember (or with '' forget) the folder for `repoId`. Returns false when storage refused. */
export function setClonePath(repoId, path) {
  try {
    const all = readAll();
    if (path) all[String(repoId)] = String(path);
    else delete all[String(repoId)];
    window.localStorage.setItem(apibooKey(SUFFIX), JSON.stringify(all));
    return true;
  } catch {
    return false;
  }
}

/** The folder to read for `repo`: its own path (a personal local link), else the one picked here. */
export function localPathOf(repo) {
  if (!repo) return '';
  return repo.path || clonePathFor(repo.id) || '';
}

const identity = (remote) => {
  if (!remote) return null;
  const host = hostOf(remote);
  const { owner, repo } = ownerRepoOf(remote);
  if (!host || !repo) return null;
  return `${host}/${String(owner || '').toLowerCase()}/${String(repo).toLowerCase().replace(/\.git$/, '')}`;
};

/** True when a clone's `origin` points at the same host/owner/repo as the linked remote. */
export function sameRepository(linkedRemote, cloneRemote) {
  const a = identity(linkedRemote);
  const b = identity(cloneRemote);
  return !!a && a === b;
}
